import React, { useState, useEffect, useRef } from "react";
import { loadStripe, Stripe, StripeElements, StripeCardNumberElement } from "@stripe/stripe-js";
import { useGetStripePublicKeyQuery } from "../../../api/queries";
import styles from "./AddNewPaymentMethodPopup.module.scss";
import { useTranslation } from "react-i18next";
import CustomButton from "../../ui/CustomButton/CustomButton";
import useAppDispatch from "../../../hooks/useAppDispatch";
import { openErrorMessage, openSuccessMessage } from "../../../store/message/messageSlice";
import { useStripeMutations } from "../../../api/mutations";
import { handleError } from "../../../helper/errorHandler";
import FullScreenLoader from "../FullScreenLoader/FullScreenLoader";

interface AddPaymentMethodPopupProps {
  onClose: () => void;
  refetch: () => void;
}

type Status = "idle" | "loading" | "success" | "error";

const STRIPE_ELEMENT_STYLE = {
  base: {
    color: "#2d2d2d",
    fontFamily: "Open sans, sans-serif",
    fontSize: "17px",
    lineHeight: "24px",
    placeholder: {
      color: "#667085",
    },
  },
  invalid: { color: "#e53e3e" },
};

const AddPaymentMethodPopup: React.FC<AddPaymentMethodPopupProps> = ({ onClose, refetch }) => {
  const { t } = useTranslation();
  const { data } = useGetStripePublicKeyQuery();
  const stripePublicKey = data?.publicKey ?? "";
  const dispatch = useAppDispatch();

  const { attach } = useStripeMutations();

  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [elements, setElements] = useState<StripeElements | null>(null);
  const [cardNumberEl, setCardNumberEl] = useState<StripeCardNumberElement | null>(null);

  const [alias, setAlias] = useState("");
  const [status, setStatus] = useState<Status>("idle");

  const cardNumberRef = useRef<HTMLDivElement>(null);
  const cardExpiryRef = useRef<HTMLDivElement>(null);
  const cardCvcRef = useRef<HTMLDivElement>(null);
  const stripeInitialized = useRef(false);

  useEffect(() => {
    if (stripeInitialized.current || !stripePublicKey) return;

    (async () => {
      const stripeInstance = await loadStripe(stripePublicKey);
      if (!stripeInstance) return;

      const elementsInstance = stripeInstance.elements();
      setStripe(stripeInstance);
      setElements(elementsInstance);
      stripeInitialized.current = true;
    })();
  }, [stripePublicKey]);

  useEffect(() => {
    if (
      !elements ||
      !cardNumberRef.current ||
      !cardExpiryRef.current ||
      !cardCvcRef.current ||
      cardNumberEl
    )
      return;

    const numberEl = elements.create("cardNumber", {
      style: STRIPE_ELEMENT_STYLE,
      placeholder: "1234 1234 1234 1234",
    });
    const expiryEl = elements.create("cardExpiry", {
      style: STRIPE_ELEMENT_STYLE,
    });
    const cvcEl = elements.create("cardCvc", {
      style: STRIPE_ELEMENT_STYLE,
    });

    numberEl.mount(cardNumberRef.current);
    expiryEl.mount(cardExpiryRef.current);
    cvcEl.mount(cardCvcRef.current);

    setCardNumberEl(numberEl);

    const handleChange = (event: { error?: { message: string } }) => {
      dispatch(openErrorMessage(event.error?.message ?? ""));
    };

    numberEl.on("change", handleChange);
    expiryEl.on("change", handleChange);
    cvcEl.on("change", handleChange);

    return () => {
      numberEl.destroy();
      expiryEl.destroy();
      cvcEl.destroy();
      setCardNumberEl(null);
      stripeInitialized.current = false;
    };
  }, [elements]);

  const handleSubmit = async () => {
    if (!stripe || !cardNumberEl) return;
    setStatus("loading");

    const billingDetails = alias.trim() ? { name: alias.trim() } : {};

    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: "card",
      card: cardNumberEl,
      billing_details: billingDetails,
    });

    if (error) {
      dispatch(openErrorMessage(error.message ?? t("SOMETHING_WENT_WRONG")));
      setStatus("error");
      return;
    }

    if (paymentMethod) {
      attach(
        {
          paymentMethodId: paymentMethod.id,
          nameOnCard: alias.trim() ? alias.trim() : null,
        },
        {
          onSuccess: () => {
            refetch();
            onClose();
            dispatch(openSuccessMessage("PaymentMethodAddedSuccessfully"));
          },
          onError: (e) => {
            setStatus("error");
            handleError(e, dispatch, t);
          },
        }
      );
    }
  };

  const isSubmitting = status === "loading";
  const isSuccess = status === "success";

  return (
    <>
      <div className={styles.container}>
        <div className={styles.popup}>
          <h2 className={styles.title}>{t("AddNewCard")}</h2>

          <div className={styles.field}>
            <label className={styles.label}>{t("CardNumber")}</label>
            <div className={styles.stripeInput} ref={cardNumberRef} />
          </div>

          <div className={styles.row}>
            <div style={{ width: "50%" }} className={styles.field}>
              <label className={styles.label}>{t("ExpirationDate")}</label>
              <div className={styles.stripeInput} ref={cardExpiryRef} />
            </div>
            <div style={{ width: "50%" }} className={styles.field}>
              <label className={styles.label}>CVC</label>
              <div className={styles.stripeInput} ref={cardCvcRef} />
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>
              {t("Alias")} <span className={styles.optional}>({t("Optional")})</span>
            </label>
            <input
              className={styles.stripeInput}
              type="text"
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              placeholder="Cardholder Name"
              disabled={isSubmitting || isSuccess}
            />
          </div>

          <div className={styles.actions}>
            <CustomButton
              text={t("Cancel")}
              type={"button"}
              typeStyle={"outline"}
              functionOnClick={onClose}
              disabled={isSubmitting}
            />
            <CustomButton
              text={t("Save")}
              type={"button"}
              typeStyle={"fill"}
              functionOnClick={handleSubmit}
              disabled={isSubmitting || isSuccess || !cardNumberEl}
            />
          </div>
        </div>
      </div>
      {status === "loading" && <FullScreenLoader />}
    </>
  );
};

export default AddPaymentMethodPopup;
